import { countryForCurrency, formatAmount, cycleSuffix } from '../lib/catalogue';

/** A product with the catalogue list amount as its display price (used until Paddle answers, and when it does not). */
const withFallback = (product, currencyCode) => {
    const code = product.amounts[currencyCode] ? currencyCode : product.currency;
    const amount = product.amounts[code];
    return {
        ...product,
        currency: code,
        price_numeric: Number.isFinite(amount) ? amount : null,
        price_display: Number.isFinite(amount) ? `${formatAmount(amount, code)} ${cycleSuffix(product)}`.trim() : '—',
        price_source: 'catalogue',
    };
};

export const paddleService = {
    /**
     * Localizes the unit price of every product through Paddle's PricePreview (net of tax) in
     * the requested currency. The country is the representative one for that currency so the
     * middleware's country-scoped CHF / USD prices apply, exactly as they will at checkout for
     * a buyer in that country. Falls back to the catalogue amounts when Paddle is not ready or
     * the preview fails.
     */
    localizePrices: async (paddleInstance, products, currencyCode = 'EUR') => {
        const fallback = products.map((p) => withFallback(p, currencyCode));
        if (!paddleInstance || products.length === 0) {
            return fallback;
        }

        try {
            const preview = await paddleInstance.PricePreview({
                items: products.map((p) => ({ priceId: p.priceId, quantity: 1 })),
                currencyCode,
                address: { countryCode: countryForCurrency(currencyCode) },
            });

            // Handle both camelCase (SDK) and snake_case (raw API) response shapes.
            const data = preview?.data || {};
            const details = data.details || data.data?.details || {};
            const lineItems = details.lineItems || details.line_items || [];

            return products.map((product) => {
                const line = lineItems.find((item) => (item.price?.id || item.price_id) === product.priceId);
                const formatted = line?.formattedTotals?.subtotal || line?.formatted_totals?.subtotal;
                const numeric = line?.totals?.subtotal;
                if (!formatted) {
                    return withFallback(product, currencyCode);
                }
                return {
                    ...product,
                    currency: currencyCode,
                    price_display: `${formatted} ${cycleSuffix(product)}`.trim(),
                    // Paddle totals are minor units as strings.
                    price_numeric: Number.isFinite(Number(numeric)) ? Number(numeric) / 100 : null,
                    price_source: 'paddle',
                };
            });
        } catch (error) {
            console.error('Paddle PricePreview failed; showing catalogue amounts.', error);
            return fallback;
        }
    },
};
